import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TournamentGroup {
    TORNEO_CLAVE: number;
    TORNEO_NOMBRE: string;
    turnos: TurnoInfo[];
}

interface TurnoInfo {
    TURNO_CLAVE: number;
    TURNO_NOME: string;
    EVENTO_CLAVE: number;
    EVENTO_NOME: string;
    TEAMBOWLERS: number;
}

interface EventBowler {
    CLAVE: number;
    FICHANAL: string | number;
    NOMBRECOMPLETO: string;
    ON_TEAM: number;
}

interface TeamMember {
    ORDEN: number;
    JUGADOR_CLAVE: number;
    NOMBRECOMPLETO: string;
    FICHANAL: string | number;
}

interface ExistingTeam {
    CLAVE: number;
    NOMBRE: string;
    members: TeamMember[];
}

const PAGE_SIZE = 15;

const MakeTeams = () => {
    const navigate = useNavigate();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    function YoutubeLite({ id, title }: { id: string; title: string }) {
        const [active, setActive] = useState(false);
        return (
            <div className="bg-white/30 rounded-2xl shadow-lg border border-white/30 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/20">
                    <p className="text-sm text-black">{title}</p>
                </div>
                <div className="relative w-full cursor-pointer" style={{ paddingBottom: '56.25%' }} onClick={() => setActive(true)}>
                    {active ? (
                        <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <>
                            <img
                                className="absolute inset-0 w-full h-full object-cover"
                                src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
                                alt={title}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-red-600 hover:bg-red-500 transition-colors rounded-xl px-5 py-3 flex items-center gap-2 shadow-lg">
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                    <span className="text-white text-sm font-semibold">Play</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const [player, setPlayer] = useState<any>(null);

    // Phase 1 — tournament selection
    const [tournaments, setTournaments] = useState<TournamentGroup[]>([]);
    const [loadingTournaments, setLoadingTournaments] = useState(true);
    const [selectedTournament, setSelectedTournament] = useState<TournamentGroup | null>(null);

    // Phase 2 — turno tabs
    const [selectedTurnoIdx, setSelectedTurnoIdx] = useState(0);

    // Bowler list
    const [bowlers, setBowlers] = useState<EventBowler[]>([]);
    const [bowlerTotal, setBowlerTotal] = useState(0);
    const [bowlerPage, setBowlerPage] = useState(1);
    const [bowlerSearch, setBowlerSearch] = useState('');
    const [bowlerLoading, setBowlerLoading] = useState(false);

    // Team state: undefined = not yet fetched, null = no team, object = has team
    const [existingTeam, setExistingTeam] = useState<ExistingTeam | null | undefined>(undefined);
    const [teamLoading, setTeamLoading] = useState(false);

    // Team creation form
    const [teamName, setTeamName] = useState('');
    const [pendingMembers, setPendingMembers] = useState<EventBowler[]>([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Edit mode (add/remove members on existing team)
    const [memberActionLoading, setMemberActionLoading] = useState<number | null>(null); // jugadorClave being acted on
    const [memberActionError, setMemberActionError] = useState('');

    // ── Auth + initial load ───────────────────────────────────────────────────
    useEffect(() => {
        const str = localStorage.getItem('loggedInPlayer');
        if (!str) { navigate('/login'); return; }
        const p = JSON.parse(str);
        setPlayer(p);

        fetch(`${BACKEND_URL}/api/teams/my-team-events/${p.CLAVE}`)
            .then(r => r.json())
            .then((rows: any[]) => {
                const map: Record<number, TournamentGroup> = {};
                for (const row of rows) {
                    if (!map[row.TORNEO_CLAVE]) {
                        map[row.TORNEO_CLAVE] = {
                            TORNEO_CLAVE: row.TORNEO_CLAVE,
                            TORNEO_NOMBRE: row.TORNEO_NOMBRE,
                            turnos: []
                        };
                    }
                    map[row.TORNEO_CLAVE].turnos.push({
                        TURNO_CLAVE: row.TURNO_CLAVE,
                        TURNO_NOME: row.TURNO_NOME,
                        EVENTO_CLAVE: row.EVENTO_CLAVE,
                        EVENTO_NOME: row.EVENTO_NOMBRE,
                        TEAMBOWLERS: row.TEAMBOWLERS
                    });
                }
                setTournaments(Object.values(map));
            })
            .catch(console.error)
            .finally(() => setLoadingTournaments(false));
    }, [BACKEND_URL, navigate]);

    // ── Derived ──────────────────────────────────────────────────────────────
    const selectedTurno = selectedTournament?.turnos[selectedTurnoIdx] ?? null;
    const totalPages = Math.ceil(bowlerTotal / PAGE_SIZE);

    // ── Fetch helpers ─────────────────────────────────────────────────────────
    const fetchBowlers = (
        torneoClave: number,
        turnoClave: number,
        search: string,
        page: number
    ) => {
        setBowlerLoading(true);
        fetch(
            `${BACKEND_URL}/api/teams/event-bowlers/${torneoClave}/${turnoClave}` +
            `?search=${encodeURIComponent(search)}&page=${page}&pageSize=${PAGE_SIZE}`
        )
            .then(r => r.json())
            .then(data => {
                setBowlers(data.bowlers || []);
                setBowlerTotal(data.total || 0);
            })
            .catch(console.error)
            .finally(() => setBowlerLoading(false));
    };

    const fetchMyTeam = (torneoClave: number, turnoClave: number, jugadorClave: number) => {
        setTeamLoading(true);
        fetch(`${BACKEND_URL}/api/teams/my-team/${torneoClave}/${turnoClave}/${jugadorClave}`)
            .then(r => r.json())
            .then(data => setExistingTeam(data.team ?? null))
            .catch(console.error)
            .finally(() => setTeamLoading(false));
    };

    // ── Event tab change triggers data reload ─────────────────────────────────
    useEffect(() => {
        if (!selectedTournament || !player) return;
        const turno = selectedTournament.turnos[selectedTurnoIdx];
        if (!turno) return;

        // Reset creation form
        setTeamName('');
        setPendingMembers([]);
        setSaveError('');
        setSaveSuccess(false);
        setExistingTeam(undefined);
        setBowlerSearch('');
        setBowlerPage(1);

        fetchBowlers(selectedTournament.TORNEO_CLAVE, turno.TURNO_CLAVE, '', 1);
        fetchMyTeam(selectedTournament.TORNEO_CLAVE, turno.TURNO_CLAVE, player.CLAVE);
    }, [selectedTournament, selectedTurnoIdx, player]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleSearchChange = (val: string) => {
        if (!selectedTournament || !selectedTurno) return;
        setBowlerSearch(val);
        setBowlerPage(1);
        fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, val, 1);
    };

    const handlePageChange = (p: number) => {
        if (!selectedTournament || !selectedTurno) return;
        setBowlerPage(p);
        fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, p);
    };

    const addMember = (bowler: EventBowler) => {
        if (!selectedTurno) return;
        if (pendingMembers.find(m => m.CLAVE === bowler.CLAVE)) return;
        if (pendingMembers.length >= selectedTurno.TEAMBOWLERS) {
            setSaveError(`Maximum team size is ${selectedTurno.TEAMBOWLERS} bowlers`);
            return;
        }
        setSaveError('');
        setPendingMembers(prev => [...prev, bowler]);
    };

    const removeMember = (clave: number) => {
        setPendingMembers(prev => prev.filter(m => m.CLAVE !== clave));
        setSaveError('');
    };

    const handleSave = async () => {
        if (!selectedTournament || !selectedTurno || !player) return;
        if (!teamName.trim()) { setSaveError('Please enter a team name'); return; }
        if (pendingMembers.length === 0) { setSaveError('Please add at least one bowler to the team'); return; }

        setSaveLoading(true);
        setSaveError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: selectedTournament.TORNEO_CLAVE,
                    eventoClave: selectedTurno.EVENTO_CLAVE,
                    turnoClave: selectedTurno.TURNO_CLAVE,
                    jugadorClave: player.CLAVE,
                    teamName: teamName.trim(),
                    memberClaves: pendingMembers.map(m => m.CLAVE)
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setSaveSuccess(true);
                // Reload team + bowler list so UI reflects new state
                fetchMyTeam(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, player.CLAVE);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setSaveError(data.error || 'Failed to create team');
            }
        } catch {
            setSaveError('An error occurred. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    const addMemberToExistingTeam = async (bowler: EventBowler) => {
        if (!selectedTournament || !selectedTurno || !existingTeam) return;
        setMemberActionLoading(bowler.CLAVE);
        setMemberActionError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/add-member`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    teamClave: existingTeam.CLAVE,
                    torneoClave: selectedTournament.TORNEO_CLAVE,
                    turnoClave: selectedTurno.TURNO_CLAVE,
                    jugadorClave: bowler.CLAVE
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                fetchMyTeam(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, player.CLAVE);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setMemberActionError(data.error || 'Failed to add bowler');
            }
        } catch {
            setMemberActionError('An error occurred. Please try again.');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const removeSelfFromTeam = async () => {
        if (!selectedTournament || !selectedTurno || !existingTeam || !player) return;
        setMemberActionLoading(player.CLAVE);
        setMemberActionError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/remove-member`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teamClave: existingTeam.CLAVE, jugadorClave: player.CLAVE })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setExistingTeam(null);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setMemberActionError(data.error || 'Failed to remove from team');
            }
        } catch {
            setMemberActionError('An error occurred. Please try again.');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const handleSelectTournament = (t: TournamentGroup) => {
        setSelectedTournament(t);
        setSelectedTurnoIdx(0);
    };

    const handleBack = () => {
        setSelectedTournament(null);
        setSelectedTurnoIdx(0);
        setBowlers([]);
        setExistingTeam(undefined);
    };

    // ── Loading spinner ───────────────────────────────────────────────────────
    if (loadingTournaments) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading team events...</p>
                </div>
            </div>
        );
    }

    // ── Phase 1: Tournament selection ─────────────────────────────────────────
    if (!selectedTournament) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-4">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="text-gray-500 hover:text-gray-700 transition"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Create Team</h1>
                            <p className="text-sm text-gray-500">Select a tournament to manage your teams</p>
                        </div>
                    </div>
                </div>
                {/* youtube tutorials */}
                <div className="mt-6">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                            <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                            </svg>
                        </div>
                        <h2 className="text-sm font-bold text-black uppercase tracking-widest">Team Creation Tutorials</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            { id: 'NO-oUJ79BJ4', title: 'Teams tutorial in english' },
                            { id: '2bovq-NRQPk', title: 'Tutorial sobre equipos en español' },
                        ].map(({ id, title }) => (
                            <YoutubeLite key={id} id={id} title={title} />
                        ))}
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 py-10">
                    {tournaments.length === 0 ? (
                        <div className="text-center py-24">
                            <div className="bg-white rounded-2xl shadow-lg p-12 inline-block">
                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <p className="text-gray-500 font-semibold text-lg mb-2">No team events found</p>
                                <p className="text-gray-400 text-sm">You are not registered in any team events.</p>
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="mt-6 bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-indigo-700 transition"
                                >
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {tournaments.map(t => (
                                <button
                                    key={t.TORNEO_CLAVE}
                                    onClick={() => handleSelectTournament(t)}
                                    className="bg-white rounded-2xl shadow-lg p-6 text-left hover:shadow-xl transition transform hover:-translate-y-1 group border-2 border-transparent hover:border-indigo-200"
                                >
                                    <div className="bg-indigo-50 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-3">{t.TORNEO_NOMBRE}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {t.turnos.map(turno => (
                                            <span
                                                key={turno.TURNO_CLAVE}
                                                className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-semibold"
                                            >
                                                {turno.EVENTO_NOME} — {turno.TURNO_NOME} · {turno.TEAMBOWLERS} bowlers
                                            </span>
                                        ))}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Phase 2: Team creation ────────────────────────────────────────────────
    const isPending = (clave: number) => pendingMembers.some(m => m.CLAVE === clave);
    const isOnThisTeam = (clave: number) =>
        existingTeam != null && existingTeam.members.some(m => m.JUGADOR_CLAVE === clave);
    const teamIsFull = existingTeam != null &&
        selectedTurno != null &&
        existingTeam.members.length >= selectedTurno.TEAMBOWLERS;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 py-5 flex items-center gap-4">
                    <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Create Team</h1>
                        <p className="text-sm text-gray-500">{selectedTournament.TORNEO_NOMBRE}</p>
                    </div>
                </div>
            </div>

            {/* Turno tabs — shown when there are multiple turnos */}
            {selectedTournament.turnos.length > 1 && (
                <div className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex overflow-x-auto">
                            {selectedTournament.turnos.map((turno, idx) => (
                                <button
                                    key={turno.TURNO_CLAVE}
                                    onClick={() => setSelectedTurnoIdx(idx)}
                                    className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition whitespace-nowrap ${selectedTurnoIdx === idx
                                            ? 'border-indigo-600 text-indigo-700'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {turno.EVENTO_NOME} — {turno.TURNO_NOME}
                                    <span className="ml-2 text-xs font-normal text-gray-400">({turno.TEAMBOWLERS} max)</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main two-panel layout */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Single-turno label */}
                {selectedTournament.turnos.length === 1 && selectedTurno && (
                    <div className="mb-4 flex items-center gap-2">
                        <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full">
                            {selectedTurno.EVENTO_NOME} — {selectedTurno.TURNO_NOME} · {selectedTurno.TEAMBOWLERS} bowlers max
                        </span>
                    </div>
                )}

                <div className="flex gap-6 items-start">
                    {/* ── Left: Bowler list ──────────────────────────────────── */}
                    <div className="w-80 flex-shrink-0">
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                            <div className="px-4 py-4 border-b border-gray-100">
                                <h2 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                                    {selectedTurno?.TURNO_NOME} Bowlers
                                </h2>
                                <input
                                    type="text"
                                    placeholder="Search name or TAT#..."
                                    value={bowlerSearch}
                                    onChange={e => handleSearchChange(e.target.value)}
                                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition"
                                />
                            </div>

                            <div className="overflow-y-auto divide-y divide-gray-50" style={{ maxHeight: '520px' }}>
                                {bowlerLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                    </div>
                                ) : bowlers.length === 0 ? (
                                    <p className="text-center text-gray-400 py-10 text-sm">No bowlers found</p>
                                ) : (
                                    bowlers.map(b => {
                                        const alreadyOnTeam = b.ON_TEAM > 0;
                                        const inPending = isPending(b.CLAVE);
                                        const onThisTeam = isOnThisTeam(b.CLAVE);
                                        const hasExistingTeam = existingTeam !== null && existingTeam !== undefined;
                                        const isLoading = memberActionLoading === b.CLAVE;

                                        return (
                                            <div key={b.CLAVE} className="flex items-center px-4 py-3 gap-3 hover:bg-gray-50 transition">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-gray-800 text-sm truncate">{b.NOMBRECOMPLETO}</p>
                                                    <p className="text-xs text-gray-400">TAT# {b.FICHANAL}</p>
                                                </div>

                                                {/* Priority: loading > onThisTeam > alreadyOnOtherTeam > pending > existingTeamFull > add */}
                                                {isLoading ? (
                                                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <svg className="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                    </span>
                                                ) : onThisTeam ? (
                                                    <span title="Already on your team" className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                                                        <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                ) : alreadyOnTeam ? (
                                                    <span title="On another team" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </span>
                                                ) : inPending ? (
                                                    <button
                                                        onClick={() => removeMember(b.CLAVE)}
                                                        title="Remove from team"
                                                        className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 hover:bg-red-100 flex items-center justify-center transition group"
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-green-600 group-hover:text-red-500 transition" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                ) : hasExistingTeam && teamIsFull ? (
                                                    <span title="Team is full" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => hasExistingTeam ? addMemberToExistingTeam(b) : addMember(b)}
                                                        title="Add to team"
                                                        className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition"
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                                    <button
                                        onClick={() => handlePageChange(bowlerPage - 1)}
                                        disabled={bowlerPage <= 1}
                                        className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-400">{bowlerPage} / {totalPages}</span>
                                    <button
                                        onClick={() => handlePageChange(bowlerPage + 1)}
                                        disabled={bowlerPage >= totalPages}
                                        className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Right: Team panel ──────────────────────────────────── */}
                    <div className="flex-1">
                        {/* Loading */}
                        {teamLoading || existingTeam === undefined ? (
                            <div className="bg-white rounded-2xl shadow-lg p-10 flex items-center justify-center min-h-[280px]">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                            </div>

                            /* Existing team view */
                        ) : existingTeam !== null ? (
                            <div className="bg-white rounded-2xl shadow-lg p-6">
                                <div className="flex items-center justify-between gap-3 mb-5">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-green-100 rounded-xl p-2.5">
                                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Your Team — {selectedTurno?.TURNO_NOME}</p>
                                            <h2 className="text-2xl font-bold text-gray-900">{existingTeam.NOMBRE}</h2>
                                        </div>
                                    </div>
                                    <span className={`text-sm font-bold px-3 py-1.5 rounded-full flex-shrink-0 ${selectedTurno && existingTeam.members.length >= selectedTurno.TEAMBOWLERS
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-amber-100 text-amber-700'
                                        }`}>
                                        {existingTeam.members.length} / {selectedTurno?.TEAMBOWLERS}
                                    </span>
                                </div>

                                {/* Member action error */}
                                {memberActionError && (
                                    <div className="mb-4 flex items-start gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        {memberActionError}
                                    </div>
                                )}

                                <div className="space-y-3">
                                    {existingTeam.members.map(m => (
                                        <div key={m.JUGADOR_CLAVE} className="flex items-center gap-4 bg-gray-50 rounded-xl px-5 py-3">
                                            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                                                {m.ORDEN}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-800">{m.NOMBRECOMPLETO}</p>
                                                <p className="text-xs text-gray-400">TAT# {m.FICHANAL}</p>
                                            </div>
                                            {m.JUGADOR_CLAVE === player?.CLAVE && (
                                                <button
                                                    onClick={removeSelfFromTeam}
                                                    disabled={memberActionLoading === player?.CLAVE}
                                                    title="Leave this team"
                                                    className="flex-shrink-0 flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition disabled:opacity-40"
                                                >
                                                    {memberActionLoading === player?.CLAVE ? (
                                                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                        </svg>
                                                    )}
                                                    Leave
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Add-more hint when team is not full */}
                                {selectedTurno && existingTeam && existingTeam.members.length < selectedTurno.TEAMBOWLERS && (
                                    <p className="mt-5 text-xs text-indigo-500 flex items-center gap-1.5 bg-indigo-50 rounded-xl px-4 py-3">
                                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Use the + buttons on the left to add more bowlers ({selectedTurno.TEAMBOWLERS - existingTeam.members.length} spot{selectedTurno.TEAMBOWLERS - existingTeam.members.length !== 1 ? 's' : ''} remaining)
                                    </p>
                                )}
                            </div>

                            /* Team creation form */
                        ) : (
                            <div className="bg-white rounded-2xl shadow-lg p-6">
                                {saveSuccess ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900 mb-2">Team Created!</h2>
                                        <p className="text-gray-500 text-sm">Your team has been saved successfully.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-6">
                                            <h2 className="text-xl font-bold text-gray-900">New Team</h2>
                                            <p className="text-sm text-gray-400 mt-0.5">{selectedTurno?.EVENTO_NOME} — {selectedTurno?.TURNO_NOME} · up to {selectedTurno?.TEAMBOWLERS} bowlers</p>
                                        </div>

                                        {/* Team name */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Team Name</label>
                                            <input
                                                type="text"
                                                placeholder="Enter your team name..."
                                                value={teamName}
                                                onChange={e => { setTeamName(e.target.value); setSaveError(''); }}
                                                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition"
                                            />
                                        </div>

                                        {/* Member counter + list */}
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-sm font-semibold text-gray-700">Team Members</h3>
                                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${selectedTurno && pendingMembers.length === selectedTurno.TEAMBOWLERS
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {pendingMembers.length} / {selectedTurno?.TEAMBOWLERS}
                                                </span>
                                            </div>

                                            {pendingMembers.length === 0 ? (
                                                <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center">
                                                    <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <p className="text-gray-400 text-sm">Use the + buttons on the left to add bowlers</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {pendingMembers.map((m, idx) => (
                                                        <div key={m.CLAVE} className="flex items-center gap-3 bg-indigo-50 rounded-xl px-4 py-3">
                                                            <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                                                                {idx + 1}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-gray-800 text-sm truncate">{m.NOMBRECOMPLETO}</p>
                                                                <p className="text-xs text-gray-400">TAT# {m.FICHANAL}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => removeMember(m.CLAVE)}
                                                                className="flex-shrink-0 text-gray-400 hover:text-red-500 transition"
                                                                title="Remove"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Error */}
                                        {saveError && (
                                            <div className="mb-4 flex items-start gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                {saveError}
                                            </div>
                                        )}

                                        {/* Save button */}
                                        <button
                                            onClick={handleSave}
                                            disabled={saveLoading || !teamName.trim() || pendingMembers.length === 0}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {saveLoading ? 'Saving...' : 'Save Team'}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MakeTeams;
